import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  bulkUpdatePermitApplications,
  createPermitApplication,
  deletePermitApplication,
  downloadPermitApplicationsCsv,
  listPermitApplications,
  type PermitApplication,
  type PermitApplicationPayload,
  type PermitApplicationStatus,
  updatePermitApplication,
} from "@/api/permit_applications";
import {
  PERMIT_APPLICATION_STATUS_ORDER,
  sortPermitApplicationsByDeadline,
} from "@/components/permit_suite/permitSuiteDisplay";
import type { PermitApplicationFocusRequest } from "@/components/permit_suite/permitSuiteNavigation";
import { useToast } from "@/hooks/useToast";
import { invalidatePermitSuitePipeline } from "@/lib/permitSuiteQueryKeys";

export type PermitTrackStatusFilter = PermitApplicationStatus | "all";

type UsePermitTrackOptions = {
  municipalityId?: number;
  focusRequest?: PermitApplicationFocusRequest | null;
  onFocusRequestHandled?: () => void;
};

export function usePermitTrack({
  municipalityId,
  focusRequest = null,
  onFocusRequestHandled,
}: UsePermitTrackOptions) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [statusFilter, setStatusFilter] = useState<PermitTrackStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<PermitApplicationStatus>("under_review");
  const [bulkDeadline, setBulkDeadline] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PermitApplication | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formApplicationNumber, setFormApplicationNumber] = useState("");
  const [formApplicantName, setFormApplicantName] = useState("");
  const [formProjectAddress, setFormProjectAddress] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formDeadline, setFormDeadline] = useState("");
  const [exportingCsv, setExportingCsv] = useState(false);

  const queryKey = useMemo(
    () => ["permit_applications", municipalityId, statusFilter, search] as const,
    [municipalityId, statusFilter, search],
  );

  const serverStatus = statusFilter === "all" ? undefined : statusFilter;

  const { data: applications = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      listPermitApplications(municipalityId!, {
        status: serverStatus,
        search: search.trim() || undefined,
      }),
    enabled: Boolean(municipalityId),
  });

  const filtered = useMemo(() => sortPermitApplicationsByDeadline(applications), [applications]);

  const focusTarget = pendingFocusId
    ? applications.find((app) => app.id === pendingFocusId)
    : null;

  const selected =
    focusTarget ??
    filtered.find((app) => app.id === selectedId) ??
    filtered[0] ??
    null;

  useEffect(() => {
    setSelectedId(null);
    setPendingFocusId(null);
    setSearch("");
    setSelectedIds([]);
    setStatusFilter("all");
    setCreateOpen(false);
    setDeleteTarget(null);
  }, [municipalityId]);

  useEffect(() => {
    if (!focusRequest) return;
    setStatusFilter("all");
    setSearch("");
    setPendingFocusId(focusRequest.applicationId);
  }, [focusRequest]);

  useEffect(() => {
    if (!pendingFocusId) return;

    const focused = applications.find((app) => app.id === pendingFocusId);
    if (focused) {
      setSelectedId(focused.id);
      setPendingFocusId(null);
      onFocusRequestHandled?.();
      return;
    }

    if (!isLoading) {
      setPendingFocusId(null);
      onFocusRequestHandled?.();
      toast({
        title: "Could not open that permit",
        description: "It may have been removed or you may not have access.",
        variant: "destructive",
      });
    }
  }, [pendingFocusId, applications, isLoading, onFocusRequestHandled, toast]);

  useEffect(() => {
    if (pendingFocusId) return;

    if (filtered.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filtered.some((app) => app.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId, pendingFocusId]);

  const invalidate = useCallback(() => {
    invalidatePermitSuitePipeline(queryClient, municipalityId!);
  }, [municipalityId, queryClient]);

  const createMutation = useMutation({
    mutationFn: (payload: PermitApplicationPayload) =>
      createPermitApplication(municipalityId!, payload),
    onSuccess: (created) => {
      invalidate();
      setCreateOpen(false);
      setFormTitle("");
      setFormApplicationNumber("");
      setFormApplicantName("");
      setFormProjectAddress("");
      setFormNotes("");
      setFormDeadline("");
      setSelectedId(created.id);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<PermitApplicationPayload> }) =>
      updatePermitApplication(municipalityId!, id, patch),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePermitApplication(municipalityId!, id),
    onSuccess: () => {
      setDeleteTarget(null);
      invalidate();
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (patch: Partial<PermitApplicationPayload>) => {
      const result = await bulkUpdatePermitApplications(municipalityId!, selectedIds, patch);
      if (result.failed.length > 0) {
        throw new Error(`Updated ${result.updated_count} of ${selectedIds.length} applications`);
      }
      return result;
    },
    onError: (error) => {
      toast({
        title: "Bulk update incomplete",
        description: error instanceof Error ? error.message : "Some updates failed",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      setSelectedIds([]);
      invalidate();
    },
  });

  const handleCreate = () => {
    if (!formTitle.trim()) return;
    createMutation.mutate({
      title: formTitle.trim(),
      application_number: formApplicationNumber.trim() || null,
      applicant_name: formApplicantName.trim() || null,
      project_address: formProjectAddress.trim() || null,
      notes: formNotes.trim() || null,
      deadline_at: formDeadline ? new Date(formDeadline).toISOString() : null,
      status: "intake",
    });
  };

  const handleExportCsv = async () => {
    if (!municipalityId) return;
    setExportingCsv(true);
    try {
      await downloadPermitApplicationsCsv(municipalityId, {
        status: serverStatus,
        search: search.trim() || undefined,
      });
    } catch {
      toast({
        title: "Export failed",
        description: "We could not download your pipeline CSV. Please try again.",
        variant: "destructive",
      });
    } finally {
      setExportingCsv(false);
    }
  };

  const toggleSelected = (id: number, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...new Set([...current, id])] : current.filter((item) => item !== id),
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? filtered.map((app) => app.id) : []);
  };

  return {
    applications,
    filtered,
    selected,
    isLoading,
    error,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    selectedIds,
    setSelectedIds,
    bulkStatus,
    setBulkStatus,
    bulkDeadline,
    setBulkDeadline,
    createOpen,
    setCreateOpen,
    deleteTarget,
    setDeleteTarget,
    formTitle,
    setFormTitle,
    formApplicationNumber,
    setFormApplicationNumber,
    formApplicantName,
    setFormApplicantName,
    formProjectAddress,
    setFormProjectAddress,
    formNotes,
    setFormNotes,
    formDeadline,
    setFormDeadline,
    exportingCsv,
    handleCreate,
    handleExportCsv,
    createMutation,
    updateMutation,
    deleteMutation,
    bulkUpdateMutation,
    statusOptions: PERMIT_APPLICATION_STATUS_ORDER,
    setSelectedId,
    toggleSelected,
    toggleSelectAll,
  };
}
